/**
 * Canonical reports-folder path for a patient / schedule row.
 *
 * Mirrors PatientPage.buildDefaultReportsPath (and the backend / Uploader.js)
 * so the derived path matches where uploads actually land:
 *   New/Reports/{schema?}/{patientName}_{EHR_ID}/{MM-DD-YY}
 * - schema segment omitted for the `public` schema
 * - patient name has `/` replaced with `-` and is trimmed (slashes would
 *   otherwise add a spurious path segment)
 * - EHR dropped when missing or e-mail-like → trailing `_`
 * - date formatted to MM-DD-YY; returns null for an unparseable/non-string
 *   date rather than guessing a wrong (or today's) folder
 *
 * The row's `reports` column is only auto-populated by the fax match/assign
 * flow, so files that arrived another way leave it blank. Detection/listing
 * code should fall back to this derived path so those reports are still found:
 *   const folder = row.reports || buildReportsFolder(row);
 */
import { supabaseSchema } from "../components/supabaseClient";
import { prompts, colid2 } from "../components/env";

// "YYYY-MM-DD" (padded or not) → "MM-DD-YY". null if it isn't a date string,
// so detection never points at a folder that doesn't match the real upload.
function formatDateFolder(dateValue) {
  if (typeof dateValue !== "string") return null;
  const m = dateValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!m) return null;
  return `${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}-${m[1].slice(2)}`;
}

export function buildReportsFolder(row) {
  if (!row) return null;

  // Name: prefer the configured first-prompt field (what uploads use), then
  // the common row aliases. Sanitize exactly like the canonical builder.
  const nameField = prompts?.[0]?.[0];
  const rawName =
    (nameField && row[nameField]) || row[colid2] || row.patient_name || row.name;
  if (!rawName) return null;
  const patientName = String(rawName).replace(/\//g, "-").trim();

  // EHR: read the configured EHR_ID field, drop when blank or e-mail-like.
  const ehrIdIndex = prompts.findIndex((p) => p[0] === "EHR_ID");
  const ehrCandidate =
    (ehrIdIndex !== -1 && row[prompts[ehrIdIndex][0]]) ||
    row.EHR_ID ||
    row.ehr_id ||
    "";
  const ehrId =
    ehrCandidate &&
    String(ehrCandidate).trim() &&
    !String(ehrCandidate).includes("@")
      ? String(ehrCandidate).trim()
      : "";

  const dateFolder = formatDateFolder(row.date_);
  const folderName = `${patientName}_${ehrId}`;
  // Prefer the patient row's own schema (cross-schema / linked-provider
  // charts stamp `_schema`); fall back to the viewer's active schema.
  const effSchema = row._schema || supabaseSchema;
  const schema = effSchema && effSchema !== "public" ? `${effSchema}/` : "";

  // Patient-level roster charts (usePatientDocument stamps `_source:'roster'`)
  // have no visit date — their reports live at the patient folder ROOT, the
  // same place the Reports modal and the fax roster-fallback write. Dated
  // visit rows still require a real date below so detection never points at
  // a wrong (or today's) folder.
  if (row._source === "roster") {
    return `New/Reports/${schema}${folderName}`;
  }

  if (!patientName || !dateFolder) return null;
  return `New/Reports/${schema}${folderName}/${dateFolder}`;
}

export default buildReportsFolder;
